<template>
  <div class="cohort-edit-wizard">
    <div class="tab-container edyst-form-group">
      <h3 class="text-center mb-4">
        {{
          editMode ? `Edit Cohort: ${this.cohortObj.name}` : "Create New Cohort"
        }}
      </h3>
      <button
        :disabled="!editMode && tabIndex !== tabs.length - 1"
        class="p-2 update-button bg-green text-white rounded-sm shadow-md px-8"
        @click="addNewCohort"
      >
        {{ editMode ? "Update" : "Save" }}
      </button>
      <div class="stepper-container my-4">
        <button
          v-for="(tab, idx) in tabs"
          :key="idx + tab"
          class="px-1 action-btn rounded-sm truncate text-sm font-normal text-white mr-2"
          :disabled="!editMode && tabIndex < idx"
          :style="
            currentTab === tab
              ? '#6259c3'
              : editMode || tabIndex > idx
              ? 'background: #9f96ff;'
              : 'background: grey'
          "
          @click="moveToTab(idx)"
        >
          {{ idx + 1 }}. {{ tab }}
        </button>
      </div>
      <!-- basic details ======================================================================== -->
      <div v-if="currentTab === 'Basic Details'" class="fadeIn">
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <div class="flex items-center">
          <label for="name"
            >Cohort Name <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <input
              id="cohort name"
              v-model="cohortObj.name"
              v-validate="'required'"
              name="cohort name"
              type="text"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("cohort name") }}
            </small>
          </div>
        </div>
        <div class="flex items-center mt-2 course-name">
          <label>Course Name <span class="text-red ml-2">*</span></label>
          <div class="w-full">
            <div class="flex w-full items-center">
              <MultiSelect
                ref="courseSelector"
                v-model="course"
                label="name"
                :options="courseOptions"
                placeholder="Select Course"
                :multiple="false"
                :class="
                  veeValidateErrors.first('course name') ? 'is-invalid' : ''
                "
              />
            </div>
            <input
              v-model="course"
              v-validate="'required'"
              type="hidden"
              name="course name"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("course name") }}
            </small>
          </div>
        </div>
        <div class="flex items-center">
          <label for="max size"
            >Maximum Size <span class="text-red ml-2">*</span></label
          >
          <div class="w-full">
            <input
              v-model="cohortObj.size_max"
              v-validate="'required'"
              type="number"
              step="1"
              name="maximum size"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("maximum size") }}
            </small>
          </div>
        </div>
        <div class="flex items-center">
          <label for="price"
            >Price (in paise, 0 if free)<span class="text-red ml-2"
              >*</span
            ></label
          >
          <div class="w-full">
            <input
              v-model="cohortObj.base_price"
              v-validate="'required'"
              type="number"
              name="price"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("price") }}
            </small>
          </div>
        </div>
        <div class="flex items-start mt-3">
          <label for="referral" class="mt-2">Referral Discount</label>
          <div class="w-full">
            <div
              class="flex items-center mb-4 rounded-sm hover:bg-grey-light px-2 cursor-pointer"
            >
              <input
                id="referral toggle"
                v-model="cohortObj.referral_enabled"
                class="mr-1"
                type="checkbox"
              />
              <label
                for="referral toggle"
                style="width: 100%;padding: 0.5rem;"
                class="select-none cursor-pointer"
                >Enable referral code discount</label
              >
            </div>
            <div
              class="mt-1"
              :class="{
                'opacity-50 select-none pointer-events-none': !cohortObj.referral_enabled,
              }"
              :style="!cohortObj.referral_enabled ? 'filter: grayscale(1)' : ''"
            >
              <label for="discount amount"
                >Discount Amount (in paisa)<span class="text-red ml-2"
                  >*</span
                ></label
              >
              <div class="relative" style="max-width: 20rem">
                <input
                  v-model="cohortObj.referral_discount"
                  v-validate="
                    cohortObj.referral_enabled
                      ? 'required|numeric|notMoreThanBasePrice'
                      : ''
                  "
                  type="number"
                  :disabled="!cohortObj.referral_enabled"
                  name="discount amount"
                  placeholder="Enter discount amount in paisa"
                  class="referral-discount"
                />
                <span
                  v-show="cohortObj.referral_discount"
                  class="inline-block flex items-center rupee-container px-3 rounded-r"
                >
                  =
                  <i class="material-icons cursor-pointer text-base ml-1"
                    >currency_rupee</i
                  >
                  {{ discountInRupees }}/-
                </span>
              </div>
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("discount amount") }}
              </small>
            </div>
          </div>
        </div>
        <div class="flex items-center">
          <label for="start date"
            >Start Date <span class="text-red ml-2">*</span></label
          >
          <div class="w-full mt-3">
            <EdystDatePicker
              v-model="cohortObj.start_date"
              :selected="
                cohortObj.start_date ? new Date(cohortObj.start_date) : null
              "
              placeholderText="Select start date"
              class="w-full"
              name="start date"
              :class="veeValidateErrors.first('start date') ? 'is-invalid' : ''"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("start date") }}
            </small>
            <input
              v-model="cohortObj.start_date"
              v-validate="'required'"
              type="hidden"
              name="start date"
            />
          </div>
        </div>
        <div class="flex items-center">
          <label for="end date"
            >End Date <span class="text-red ml-2">*</span></label
          >
          <div class="w-full mt-3">
            <EdystDatePicker
              v-model="cohortObj.end_date"
              :selected="
                cohortObj.end_date ? new Date(cohortObj.end_date) : null
              "
              placeholderText="Select end date"
              class="w-full"
              :class="veeValidateErrors.first('end date') ? 'is-invalid' : ''"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("end date") }}
            </small>
            <input
              v-model="cohortObj.end_date"
              v-validate="'required'"
              type="hidden"
              name="end date"
            />
          </div>
        </div>
        <div class="flex items-start">
          <label for="end date" class="mt-4"
            >Thumbnail <span class="text-red ml-2">*</span></label
          >
          <div class="w-full mt-3">
            <ThumbnailGenerator
              v-model="cohortObj.thumbnail_url"
              :text="cohortObj.name"
              context_type="default_cohort"
              name="thumbnail"
              style="width:15rem"
            />
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("thumbnail") }}
            </small>
            <input
              v-model="cohortObj.thumbnail_url"
              v-validate="'required'"
              type="hidden"
              name="thumbnail"
            />
          </div>
        </div>
      </div>
      <!-- Cohort Type & Tags selector =========================================== -->
      <div v-if="currentTab === 'Cohort Type & Tags'" class="fadeIn">
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <div class="flex justify-start items-start pt-4">
          <div class="flex-no-shrink">
            Select Cohort Type<span class="text-red ml-2">*</span>
          </div>
          <div class="w-full px-8">
            <button
              :class="{
                'selected shadow-lg': cohortObj.cohort_type === 'default',
              }"
              class="cohort-type-button mx-auto shadow-md"
              @click="selectCohortType('default')"
            >
              <span>Default</span><span>The default edyst experience</span>
            </button>
            <button
              :class="{
                'selected shadow-lg':
                  cohortObj.cohort_type === 'daily_challenge',
              }"
              class="cohort-type-button mx-auto mt-8 shadow-md"
              @click="selectCohortType('daily_challenge')"
            >
              <span>Daily Challenge</span>
              <span>Topics/Questions are released on a timely basis</span>
            </button>
            <button
              :class="{
                'selected shadow-lg': cohortObj.cohort_type === 'bootcamp',
              }"
              class="cohort-type-button mx-auto mt-8 shadow-md"
              @click="selectCohortType('bootcamp')"
            >
              <span>Bootcamp</span>
              <span>Divide content into phases</span>
            </button>
          </div>
        </div>
        <div class="flex justify-start items-start mt-8">
          <div class="flex-no-shrink">
            Select Tags
          </div>
          <div class="bg-white rounded px-8 w-full">
            <MultiSelect
              ref="cohortTags"
              v-model="cohortObj.cohort_tags"
              :options="allCohortTags"
              track-by="id"
              label="name"
              tag-placeholder="Select Tag"
              placeholder="Select or add a tag"
              :multiple="true"
              :taggable="true"
              @tag="addNewCohortTag"
            />
          </div>
        </div>
        <input
          v-model="cohortObj.cohort_type"
          v-validate="'required'"
          type="hidden"
          name="cohort type"
        />
        <small class="err-msg text-red block mt-2 cohort-type-err-message">
          {{
            veeValidateErrors.first("cohort type")
              ? "* Please select cohort type"
              : ""
          }}
        </small>
      </div>

      <!-- Mentor selector -->
      <div v-if="currentTab === 'Mentors'" class="fadeIn mentors-tab">
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <div class="mentors">
          <p v-if="mentors.length === 0" class="text-center mt-8">
            No mentor added!
          </p>
          <div
            v-for="(mentor, index) in mentors"
            :key="index"
            class="w-full relative"
          >
            <div class="mentor-container shadow-md">
              <div class="flex items-center w-full mb-2">
                <label :for="`add-mentor-${index}`" class="w-1/4"
                  >Mentor Email:</label
                >
                <div class="w-3/4 mr-4">
                  <input
                    :id="`add-mentor-${index}`"
                    v-model="mentor.email"
                    type="text"
                    class="w-full"
                  />
                </div>
              </div>
              <div class="flex items-center w-full">
                <label class="w-1/4" @click="focusDropdown(index)"
                  >Add Mentor Tags:</label
                >
                <div class="bg-white rounded w-3/4  mr-4 mb-3">
                  <MultiSelect
                    ref="tagsSelecter"
                    v-model="mentor.tags"
                    :options="allTags"
                    tag-placeholder="Add this as new tag"
                    placeholder="Search or add a tag"
                    :multiple="true"
                    :taggable="true"
                    @tag="addNewMentorTag($event, index)"
                  />
                </div>
              </div>
              <div class="remove-mentor">
                <button
                  class="rounded p-1 hover:bg-grey-light hover:text-white text-xs"
                  @click="removeMentor(index)"
                >
                  &#x274C;
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="text-right mt-2">
          <button
            class="rounded px-3 py-2 bg-grey hover:text-white action-btn text-white shadow-md"
            @click="addNewMentor"
          >
            + Add Mentor
          </button>
        </div>
      </div>

      <div
        v-if="
          currentTab === 'Welcomer' &&
            cohortObj.data &&
            cohortObj.data.welcomer_details
        "
        class="fadeIn"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <p class="text-sm text-grey-dark mb-4">
          * To enable welcomer message please add welcomer email, online message
          and offine message. or
          <button
            class="px-2 bg-grey-light rounded-sm font-normal"
            @click="clearWelcomerDetails"
          >
            clear
          </button>
          all fields to disable it.
        </p>
        <div class="flex items-baseline">
          <label for="welcomer email" class=""
            >Enter Welcomer Email<small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("welcomer email") }}
            </small></label
          >
          <input
            id="welcomer email"
            v-model.trim="cohortObj.data.welcomer_details.email"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter welcomer email"
            name="welcomer email"
            type="email"
          />
        </div>

        <div class="flex items-baseline">
          <label for="online message" class=""
            >Online Message
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("online message") }}
            </small></label
          >
          <textarea
            id="online message"
            v-model.trim="cohortObj.data.welcomer_details.online_message"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter online message"
            class="w-2/3"
            name="online message"
            style="height: 10rem"
          ></textarea>
        </div>

        <div class="flex items-baseline">
          <label for="offline message" class=""
            >Offline Message
            <small class="err-msg text-red block mt-2">
              {{ veeValidateErrors.first("offline message") }}
            </small></label
          >
          <textarea
            id="offline message"
            v-model.trim="cohortObj.data.welcomer_details.offline_message"
            v-validate="welcomerDetailsAdded ? 'required' : ''"
            placeholder="Enter offline message"
            name="offline message"
            style="height: 10rem"
          ></textarea>
        </div>
      </div>

      <div
        v-if="currentTab === 'Settings' && cohortObj.data && cohortObj.data"
        class="fadeIn settings"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <div class="flex">
          <label>Pop-Up Form</label>
          <MultiSelect
            ref="tagsSelecter"
            v-model="cohortFormKey"
            :options="formKeysOptions"
            placeholder="Select Pop-Up form"
          />
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            User Chat
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="user chat enable"
              v-model="cohortObj.data.disableUserChat"
              :value="false"
              type="radio"
              name="user chat"
            />
            <label for="user chat enable">Enable</label>
            <input
              id="user chat disable"
              v-model="cohortObj.data.disableUserChat"
              :value="true"
              type="radio"
              name="user chat"
            />
            <label for="user chat disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Group Chat
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="group chat enable"
              v-model="cohortObj.data.disableGroupChat"
              :value="false"
              type="radio"
              name="group chat"
            />
            <label for="group chat enable">Enable</label>
            <input
              id="group chat disable"
              v-model="cohortObj.data.disableGroupChat"
              :value="true"
              type="radio"
              name="group chat"
            />
            <label for="group chat disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Notifications Events & Toasts
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="notification enable"
              v-model="cohortObj.data.disableNotificationEvents"
              :value="false"
              type="radio"
              name="notification"
            />
            <label for="notification enable">Enable</label>
            <input
              id="notification disable"
              v-model="cohortObj.data.disableNotificationEvents"
              :value="true"
              type="radio"
              name="notification"
            />
            <label for="notification disable">Disable</label>
          </div>
        </div>
        <div class="flex items-center justify-start py-2">
          <label class="w-1/4">
            Email Notifications
          </label>
          <div class="flex justify-start items-center w-full my-2">
            <input
              id="email enable"
              v-model="cohortObj.data.disableNotificationEmails"
              :value="false"
              type="radio"
              name="email notification"
            />
            <label for="email enable">Enable</label>
            <input
              id="email disable"
              v-model="cohortObj.data.disableNotificationEmails"
              :value="true"
              type="radio"
              name="email notification"
            />
            <label for="email disable">Disable</label>
          </div>
        </div>
        <div v-if="editMode" class="flex items-center justify-start">
          <label class="">Peer Chat Cohorts</label>
          <div class="w-full">
            <CohortMultiselect
              v-model="peerCohortChatMap"
              :skipCohortID="cohortObj.id"
              @input="filterPeerCohortMap"
            />
          </div>
        </div>
      </div>

      <div
        v-if="currentTab === 'Refresh' && cohortObj.data && cohortObj.data"
        class="fadeIn"
      >
        <div v-if="editMode">
          <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
            Other Actions
          </h4>
          <div class="flex items-center mt-2 course-name py-4">
            <label>Refresh Cohort path and user paths</label>
            <div class="w-full flex items-center justify-between">
              <h4 class="font-bold">{{ course.name }}</h4>
              <button
                v-if="editMode"
                :disabled="!course || refreshPathsJobScheduled"
                class="w-full text-white ml-2 rounded-sm action-btn font-normal shadow-md px-4"
                style="width: 10rem;"
                @click="refreshCourse"
              >
                Refresh Now
              </button>
              <div
                v-if="refreshPathsJobScheduled && !refreshStatus"
                title="Refresh user paths in progress"
                class="loading-icon mx-4 hover:bg-transparent focus:bg-transparent"
              >
                <img src="../../assets/icons/svg/reload.svg" />
              </div>
              <div v-if="refreshStatus" class="mx-4">
                <span
                  v-if="refreshStatus === 'success'"
                  title="Last refresh succeeded"
                  class="text-green text-3xl"
                  >✔</span
                >
                <span
                  v-else
                  title="Last refresh failed"
                  class="text-red failed-indicator text-3xl"
                  >✗</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="currentTab === 'Social Share' && cohortObj.data"
        class="fadeIn"
      >
        <h4 class="text-center font-normal mb-4 border-b pb-2 text-xl">
          {{ currentTab }}
        </h4>
        <div class="flex items-center mb-4 rounded-sm hover:bg-grey-light px-2">
          <input
            id="enable social share"
            v-model="cohortObj.social_sharing_enabled"
            class="mr-4"
            type="checkbox"
          />
          <label
            for="enable social share"
            style="width: 100%;padding: 0.5rem;"
            class="cursor-pointer select-none"
            >Enable social sharing popup for the cohort</label
          >
        </div>
        <div
          class="fadeIn"
          :class="{
            'social-sharing-disabled pointer-events-none select-none': !cohortObj.social_sharing_enabled,
          }"
        >
          <div class="flex items-start">
            <div style="width: 30%">
              <label for="share message" style="width: 100%;" class="mt-3"
                >Share Message <span class="text-red">*</span></label
              >
              <div class="text-xs text-grey-dark">
                Only plain text is accepted<br />
                New lines can be added
              </div>
            </div>
            <div class="w-full">
              <textarea
                id="share message"
                v-model="cohortObj.social_sharing_message"
                placeholder="Enter share message"
                class="w-2/3"
                style="width: 100%;height: 10rem;"
              ></textarea>
              <span
                class="text-xs text-grey-dark text-right block"
                :class="{ 'text-red-light': shareMessageCharCount > 140 }"
                >Characters count: <span>{{ shareMessageCharCount }}</span
                >/140
              </span>
            </div>
          </div>
          <div class="flex items-start">
            <label for="signup-url" class="mt-3"
              >Signup URL <span class="text-red">*</span></label
            >
            <div class="w-full">
              <textarea
                id="signup-url"
                v-model="cohortObj.social_sharing_signup_url"
                v-validate="'url'"
                placeholder="Enter signup URL"
                style="width:100%;height: 4rem;"
                name="Signup URL"
              ></textarea>
              <small class="err-msg text-red block mt-2">
                {{ veeValidateErrors.first("Signup URL") }}
              </small>
            </div>
          </div>
        </div>
      </div>
      <!-- navigation buttons -->
      <div class="text-right mt-4 navigation-buttons-container">
        <button
          class="action-btn font-normal text-white px-8 rounded-sm mr-2 shadow-md"
          :disabled="!hasPrevTab"
          @click="validateAndNavigate('prev')"
        >
          ‹‹ Prev
        </button>
        <button
          class="action-btn font-normal text-white px-8 rounded-sm ml-2 shadow-md"
          :disabled="!hasNextTab"
          @click="validateAndNavigate('next')"
        >
          Next ››
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// import VueSelect from "vue-select"
import MultiSelect from "vue-multiselect"
import { Validator } from "vee-validate"
import siteConfig from "@/utils/siteConfig"
import { getCohortTags } from "@/apis/cohorts/cohorts"
import CohortMultiselect from "@/components/shared/CohortMultiselect"
import { hasKey } from "@/utils/objectUtil"
import EdystDatePicker from "@/components/ReactDatePicker/DatePicker"
import ThumbnailGenerator from "@/components/shared/ThumbnailGenerator"

export default {
  components: {
    // VueSelect,
    CohortMultiselect,
    MultiSelect,
    EdystDatePicker,
    ThumbnailGenerator,
  },
  props: {
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "basicDetails",
      tabs: [
        "Basic Details",
        "Cohort Type & Tags",
        "Mentors",
        "Welcomer",
        "Settings",
        "Refresh",
        "Social Share",
      ],
      tabIndex: 0,
      coupon: undefined,
      course: undefined,
      // ///////////////////////////////////////////
      couponOptions: [],
      courseOptions: [],
      cohortObj: {
        size_max: null,
        coupons: [],
        pre_assessment_start_date: null,
        data: {
          mentors: [
            {
              email: undefined,
              tags: [],
            },
          ],
          disableUserChat: false,
          disableGroupChat: true,
          disableNotificationEmails: true,
          disableNotificationEvents: false,
          welcomer_details: {
            email: "",
            offline_message: "",
            online_message: "",
          },
        },
        course_id: "",
        slug: "",
        referral_enabled: false,
        referral_discount: null,
        base_price: null,
        end_date: "",
        name: "",
        cutoff: null,
        certificate_id: null,
        cohort_type: "",
        cohort_tags: [],
        certificate_enabled: false,
        start_date: "",
        pre_assessment_end_date: null,
        is_archived: false,
        cutoff_json: { game: 0, video: 0, mcq: 0, code: 0, priority: 0 },
        social_sharing_enabled: false,
        social_sharing_message:
          "Hi! I'm inviting you to Edyst. Find exciting courses and internships with certificates.",
        social_sharing_signup_url: "https://edyst.com",
        thumbnail_url: "",
      },
      formKeysOptions: [],
      cohortFormKey: undefined,
      peerCohortChatMap: undefined,
      prevPeerCohortChatMap: undefined,
      userID: "",
      uuid: "",
      welcomerEmail: "",
      onlineWelcomeMessage: "",
      offlineWelcomeMessage: "",
      mentors: [],
      allTags: [],
      allCohortTags: [],
      refreshPathsJobScheduled: false,
      refreshStatus: undefined,
      refreshtimeoutId: "",
    }
  },
  computed: {
    discountInRupees() {
      return this.cohortObj && this.cohortObj.referral_discount
        ? this.cohortObj.referral_discount / 100
        : 0
    },
    welcomerDetailsAdded() {
      /* checks if any of the input fields has any data
      if true then mark others as required in v-validate attr */
      return (
        this.cohortObj.data.welcomer_details &&
        (this.cohortObj.data.welcomer_details.email ||
          this.cohortObj.data.welcomer_details.online_message ||
          this.cohortObj.data.welcomer_details.offline_message)
      )
    },
    hasNextTab() {
      return this.tabIndex < this.tabs.length - 1
    },
    hasPrevTab() {
      return this.tabIndex > 0
    },
    currentTab() {
      return this.tabs[this.tabIndex]
    },
    shareMessageCharCount() {
      if (
        this.cohortObj.social_sharing_enabled &&
        this.cohortObj.social_sharing_message
      ) {
        return this.cohortObj.social_sharing_message.length
      }
      return 0
    },
  },
  created() {
    if (this.editMode) {
      this.getCohortData()
    } else {
      /* remove Refresh tab when creating a cohort */
      this.tabs = this.tabs.filter(t => t !== "Refresh")
      this.getCouponList()
      this.getCourseList()
      this.fetchCohortTags()
    }
    this.getFormKeys()
  },
  mounted() {
    Validator.extend("notMoreThanBasePrice", {
      getMessage:
        "Referral discount amount can not be greater than course price",
      validate: value => value <= this.cohortObj.base_price,
    })
  },
  methods: {
    clearWelcomerDetails() {
      this.cohortObj.data.welcomer_details = {
        email: "",
        online_message: "",
        offline_message: "",
      }
    },
    validateAndNavigate(way = "next") {
      if (
        this.currentTab === "Welcomer" &&
        this.cohortObj.data.welcomer_details &&
        this.cohortObj.data.welcomer_details.email &&
        (!this.cohortObj.data.welcomer_details.online_message ||
          !this.cohortObj.data.welcomer_details.offline_message)
      ) {
        this.$noty.error(
          "You added email for welcomer, please also add both online and offline messages else remove welcomer email"
        )
        return
      }
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.$noty.error("Please fill all the required fields marked with *")
          return
        }
        if (way === "next" && this.hasNextTab) {
          this.tabIndex += 1
        } else if (way === "prev" && this.hasPrevTab) {
          this.tabIndex -= 1
        }
      })
    },
    handleUpdateClick() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.$noty.error("Please fill all the required fields marked with *")
          return
        }
        this.addNewCohort()
      })
    },
    moveToTab(tabIndex) {
      if (!this.editMode && tabIndex > this.tabIndex) {
        return
      }
      this.tabIndex = tabIndex
    },
    selectCohortType(type) {
      this.cohortObj.cohort_type = type
    },
    addNewMentorTag(tag, index) {
      this.mentors[index].tags.push(tag)
      if (!this.allTags.includes(tag)) {
        this.allTags.push(tag)
      }
      this.focusDropdown(index)
    },
    filterPeerCohortMap() {
      if (this.peerCohortChatMap) {
        const newPeerCohortChatMap = {}
        /* modify the dict to the format expected in backend */
        Object.keys(this.peerCohortChatMap).forEach(cid => {
          newPeerCohortChatMap[cid] = {
            cohort_name: this.peerCohortChatMap[cid].name,
          }
        })
        this.peerCohortChatMap = newPeerCohortChatMap
      }
    },
    refreshCourse() {
      this.refreshStatus = undefined
      if (!this.cohortObj.course_id) return
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}admin/cohorts/${this.cohortObj.id}/refresh`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
        params: { dry_run: false },
      })
        .then(res => {
          if (res.status === 200) {
            // check first status after 10 sec
            setTimeout(() => {
              this.fetchRefreshPathsStatus()
            }, 10 * 1000)
            this.uuid = res.data.uuid
            this.refreshPathsJobScheduled = true
          }
        })
        .catch(err => {
          alert("Failed!")
          throw new Error(err)
        })
    },
    fetchRefreshPathsStatus() {
      this.refreshtimeoutId = setTimeout(() => {
        this.fetchRefreshPathsStatus()
      }, 10 * 1000) // check every 10 seconds
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/refresh_status/${this.cohortObj.id}/${this.uuid}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          if (res.data.status === true) {
            if (res.data.message === "successful") {
              this.refreshStatus = "success"
              clearTimeout(this.refreshtimeoutId)
              this.refreshPathsJobScheduled = false
              this.$noty.success("Refresh job was successful.")
            } else if (res.data.message.includes("failed")) {
              clearTimeout(this.refreshtimeoutId)
              this.refreshPathsJobScheduled = false
              this.refreshStatus = "failed"
              this.$noty.error(
                `Refresh job was failed!. Reason: ${res.data.details}`
              )
            }
          }
        })
        .catch(err => {
          this.$noty.error("Failed to get refresh job status: ", err)
          clearTimeout(this.refreshtimeoutId)
          this.refreshPathsJobScheduled = false
          this.refreshStatus = "failed"
        })
    },
    addNewMentor() {
      this.mentors.push({
        email: undefined,
        tags: [],
      })
    },
    focusDropdown(index) {
      this.$nextTick(() => {
        /* focus newly added select box */
        this.$refs.tagsSelecter[index].$el.focus()
      })
    },
    removeMentor(index) {
      this.mentors.splice(index, 1)
    },
    addPeerCohortId(peerCohortId) {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const peerCohortData = res.data.data || {}
        if (!peerCohortData.peerCohortChatMap) {
          peerCohortData.peerCohortChatMap = {}
        }
        const { peerCohortChatMap } = peerCohortData
        if (peerCohortChatMap[this.cohortObj.id]) {
          return
        }
        peerCohortChatMap[this.cohortObj.id] = {
          cohort_name: this.cohortObj.name,
          disableGroupChat: this.disableGroupChat,
          disableUserChat: this.disableUserChat,
        }

        this.axios({
          method: "patch",
          url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
          data: { data: peerCohortData },
        })
          .then(() => {
            console.log("added peer cohort ID:", peerCohortId)
          })
          .catch(err => {
            alert("Failed!", err)
            throw new Error(err)
          })
      })
    },
    deletePeerCohortId(peerCohortId) {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const peerCohortData = res.data.data || {}
        if (!peerCohortData.peerCohortChatMap) {
          peerCohortData.peerCohortChatMap = {}
        }
        const { peerCohortChatMap } = peerCohortData
        if (peerCohortChatMap[this.cohortObj.id]) {
          delete peerCohortChatMap[this.cohortObj.id]
        } else {
          return
        }

        this.axios({
          method: "patch",
          url: `${siteConfig.siteURL}cohorts/${peerCohortId}`,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
          data: { data: peerCohortData },
        })
          .then(() => {
            console.log("deleted peer cohort ID:", peerCohortId)
          })
          .catch(err => {
            alert("Failed!")
            throw new Error(err)
          })
      })
    },
    async addNewCohort() {
      if (!this.editMode && this.tabIndex !== this.tabs.length - 1) {
        this.$noty.error("Please Fill all the details before saving.")
        return
      }
      if (this.prevPeerCohortChatMap) {
        Object.keys(this.prevPeerCohortChatMap).forEach(prevPeerCohortId => {
          if (
            !this.peerCohortChatMap ||
            !this.peerCohortChatMap[prevPeerCohortId]
          ) {
            this.deletePeerCohortId(prevPeerCohortId)
          }
        })
      }

      if (this.peerCohortChatMap) {
        Object.keys(this.peerCohortChatMap).forEach(peerCohortId => {
          if (
            !this.prevPeerCohortChatMap ||
            !this.prevPeerCohortChatMap[peerCohortId]
          ) {
            this.addPeerCohortId(peerCohortId)
          }
        })
      }
      if (this.cohortObj.social_sharing_enabled) {
        if (
          !this.cohortObj.social_sharing_message ||
          !this.cohortObj.social_sharing_signup_url
        ) {
          this.$noty.error(
            "Please add share message and signup url for social sharing"
          )
          return
        }
      }
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }

      const obj = {
        ...JSON.parse(JSON.stringify(this.cohortObj)),
        start_date: new Date(this.cohortObj.start_date).toUTCString(),
        end_date: new Date(this.cohortObj.end_date).toUTCString(),
        course_id: this.course.id,
        coupons:
          this.coupon && this.coupon.length > 0
            ? this.coupon.map(item => item.id)
            : [],
        cutoff_json: {
          game: 0,
          video: 0,
          mcq: 0,
          code: 0,
          priority: 0,
        },
        cohort_tags: this.cohortObj.cohort_tags.map(t => t.name),
      }

      /* post above validation if we find that welcomer email is not
       present then we delete welcomer_details key from data */
      if (obj.data.welcomer_details && !obj.data.welcomer_details.email) {
        Reflect.deleteProperty(obj.data, "welcomer_details")
      }

      obj.data.user_forms = {
        ...obj.user_forms,
        home_popup: this.cohortFormKey,
      }

      obj.data.peerCohortChatMap = this.peerCohortChatMap

      if (this.mentors) {
        // Removing mentors with empty email address
        let mentors = this.mentors.filter(item => item.email)
        mentors = mentors.map(mentor => {
          mentor.tags = mentor.tags.map(tag => tag.trim())
          return mentor
        })
        obj.data.mentors = mentors
      } else {
        obj.data.mentors = []
      }

      const url = this.editMode
        ? `${siteConfig.siteURL}cohorts/${this.$route.params.id}`
        : `${siteConfig.siteURL}cohorts/`
      this.axios({
        method: this.editMode ? "PUT" : "POST",
        url,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
        data: obj,
      }).then(res => {
        if (!res.data.status) {
          this.$swal({
            icon: "error",
            text: res.data.msg,
          })
        } else {
          this.prevPeerCohortChatMap = this.peerCohortChatMap
          this.$noty.info(`Cohort ${this.editMode ? "Updated" : "Saved"}! ✔`)
          if (!this.editMode) {
            this.$router.push("/cohort/")
          }
        }
      })
    },
    getCouponList() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}coupons/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        this.couponOptions = res.data.items
        if (this.editMode) {
          this.coupons = this.couponOptions.find(
            c => c.id === this.cohortObj.coupons
          )
        }
      })
    },
    getCourseList() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}courses/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        this.courseOptions = res.data.items
        if (this.editMode) {
          this.course = this.courseOptions.find(
            c => c.id === this.cohortObj.course_id
          )
        }
      })
    },
    getFormKeys() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}content`,
      }).then(res => {
        this.formKeysOptions = res.data.filter(item =>
          item.startsWith("form-schema-")
        )
      })
    },
    getCohortData() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/${this.$route.params.id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const cohortObj = res.data
        this.coupon = cohortObj.coupons || []
        cohortObj.start_date = cohortObj.start_date
        cohortObj.end_date = cohortObj.end_date
        this.cohortFormKey =
          (cohortObj.data &&
            cohortObj.data.user_forms &&
            cohortObj.data.user_forms.home_popup) ||
          undefined
        if (!hasKey(cohortObj.data, "disableGroupChat")) {
          cohortObj.data.disableGroupChat = true
        }
        if (!hasKey(cohortObj.data, "disableUserChat")) {
          cohortObj.data.disableUserChat = false
        }
        if (!hasKey(cohortObj.data, "disableNotificationEvents")) {
          cohortObj.data.disableNotificationEvents = false
        }
        if (!hasKey(cohortObj.data, "disableNotificationEmails")) {
          cohortObj.data.disableNotificationEmails = false
        }

        if (
          cohortObj.data &&
          cohortObj.data.mentors &&
          cohortObj.data.mentors.length > 0
        ) {
          this.mentors = cohortObj.data.mentors
        } else {
          this.addNewMentor()
        }
        if (cohortObj.data && cohortObj.data.mentors) {
          const { mentors } = cohortObj.data
          let selectedTags = []
          for (let i = 0; i < mentors.length; i++) {
            selectedTags.push(...mentors[i].tags)
          }
          selectedTags = new Set(selectedTags)
          this.allTags = [...selectedTags]
        }
        if (!hasKey(cohortObj.data, "welcomer_details")) {
          cohortObj.data.welcomer_details = {
            email: "",
            offline_message: "",
            online_message: "",
          }
        }

        this.peerCohortChatMap =
          (cohortObj.data && cohortObj.data.peerCohortChatMap) || undefined
        this.prevPeerCohortChatMap = this.peerCohortChatMap

        this.cohortObj = cohortObj

        this.getCouponList()
        this.getCourseList()
        window.pageTitle = `Edit - ${this.cohortObj.name}`
        this.fetchCohortTags(true)
      })
    },
    addNewCohortTag(item) {
      this.cohortObj.cohort_tags.push({ id: item, name: item })
    },
    fetchCohortTags(mapsTags = false) {
      getCohortTags()
        .then(res => {
          this.allCohortTags = res.data.items
          if (mapsTags) {
            /**
             * tag details are mapped from tag list api as
             * cohort details api doesn't return tag name
             */
            this.cohortObj.cohort_tags = this.allCohortTags.filter(item =>
              this.cohortObj.cohort_tags.find(id => id === item.id)
            )
          }
        })
        .catch(err => {
          console.error(err)
          this.$noty.error(
            "Failed to fetch cohort tags, you may not be able to select existing cohort tags"
          )
        })
    },
  },
}
</script>

<style scoped>
.cohort-edit-wizard {
  color: #424242;
}

.rupee-container {
  position: absolute;
  right: 0.5rem;
  bottom: 0;
  height: 35px;
}
.referral-discount::-webkit-outer-spin-button,
.referral-discount::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.referral-discount {
  -moz-appearance: textfield;
}

.tab-container {
  position: relative;
  width: 100%;
  max-width: 60rem;
  min-height: 90vh;
  margin: auto;
  margin-bottom: 5rem;
  padding: 2rem 2rem 5rem;
  box-shadow: 0 0 25px silver;
}

.navigation-buttons-container {
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  background: white;
}

.mentors {
  overflow: auto;
  height: 80%;
}

.edyst-form-group input,
.edyst-form-group label {
  color: #424242;
}

.edyst-form-group label {
  width: 30%;
  padding: 0;
  text-align: left;
}

.cohort-type-button {
  display: flex;
  width: 100%;
  padding: 1rem;
  border-radius: 3px;
  font-weight: normal;
  justify-content: space-between;
}

.cohort-type-button span:first-child {
  width: 30%;
  font-weight: bold;
  text-align: center;
}

.cohort-type-button span:last-child {
  width: 70%;
  text-align: left;
}

.cohort-type-button.selected {
  border: 1px solid #00d925;
  color: green;
  background: #f9fff9;
}

.cohort-type-button:focus:not(.selected),
.cohort-type-button:hover:not(.selected),
.cohort-type-button:active:not(.selected) {
  color: #525252;
  background: #f0f0f061;
}

.mentor-container {
  height: auto;
  margin-top: 1rem;
  padding: 0.5rem 2rem;
  border: 1px solid #dbdbdb;
  border-radius: 3px;
}

.edyst-form-group input[type="radio"] {
  width: 1.2rem;
  height: 1.2rem;
  margin-right: 0.5rem;
}

.cutoff-container {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #cac7c7;
  background-color: #efefef;
}

.action-btn {
  width: auto;
  height: 2rem;
  background: #6259c3;
}

.cohort-type-err-message {
  max-width: 35rem;
  margin: auto;
  margin-top: 1rem;
}

.update-button {
  position: absolute;
  top: 2rem;
  right: 2rem;
  margin: 0;
}

.settings input[type="radio"] ~ label {
  width: 10%;
}

.course-create-input {
  width: 55%;
  margin: auto;
  margin-bottom: 8px;
}

.loading-icon {
  width: 2rem;
  height: 2rem;
}

.loading-icon img {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  animation: spin 1.5s infinite linear;
}

.social-sharing-disabled {
  opacity: 0.2;
}

button:disabled {
  background: grey;
}

.remove-mentor {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}

.mentors-tab {
  height: 80%;
}

@media screen and (max-height: 700px) {
  .mentors-tab {
    height: 70%;
  }
}

@media screen and (min-height: 900px) {
  .mentors-tab {
    height: 90%;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<!-- override for close button of vue-select tag close button -->
<style>
.mentors-container .v-select .selected-tag .close {
  margin: 0;
}

.edyst-form-group .course-name .multiselect__tags,
.edyst-form-group .course-name .multiselect__input,
.edyst-form-group .course-name .multiselect__single,
.edyst-form-group .mentor-container .multiselect__tags,
.edyst-form-group .mentor-container .multiselect__input,
.edyst-form-group .mentor-container .multiselect__single,
.edyst-form-group .settings .multiselect__tags,
.edyst-form-group .settings .multiselect__input,
.edyst-form-group .settings .multiselect__single {
  border: none;
  background: #f2f7fa;
  font-size: 1rem;
}

.edyst-form-group .datetime-picker {
  border-radius: 3px;
}

.edyst-form-group .datetime-picker #tj-datetime-input {
  margin-top: 0;
}
</style>
